import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import {
  Alert,
  Button,
  Dialog,
  Headline,
  Input,
  Paragraph,
  RadioInput,
  TextArea,
} from "@fronterahealth/frontera-ui-components";

import { useReportExportDataQuery, useUpsertReportPayorMetadataMutation } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import {
  CatalightTierBounds,
  CatalightTierKey,
  CatalightTierRecommendation,
  ServiceDisplay,
  isNoTreatmentRecommended,
  resolveCatalightRecommendations,
  resolveServiceDisplay,
} from "@components/ReportCompletionAndExport/catalightServiceType";
import { notifyError } from "@components/notifications/notifications";

// Read-only labels for the Clinical Recommendations rows (matches the design).
const REC_ROW_LABELS: Record<CatalightTierKey, string> = {
  para: "Paraprofessional Hours (per month):",
  mid: "Mid-Level Hours (per month):",
  high: "High-Level Hours (per month):",
  group: "Group Care Hours (per month):",
};

// Labels for the editable Updated Offer inputs. "hrs/mo" makes the monthly unit
// explicit.
const OFFER_INPUT_LABELS: Record<CatalightTierKey, string> = {
  para: "Paraprofessional hrs/mo",
  mid: "Mid-level hrs/mo",
  high: "High-level hrs/mo",
  group: "Group care hrs/mo",
};

const toNum = (value: string): number | null => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Negative or non-numeric values are always invalid for an hour field.
const numericError = (value: string): string | undefined => {
  const n = toNum(value);
  return n === null || n < 0 ? "Enter a valid number" : undefined;
};

// The offered hours must stay within the tier's clinical recommendation: an exact
// recommendation must be matched exactly; a range must be honored as min–max.
// `String(n)` keeps whole numbers clean in the messages ("4", not "4.0").
const boundsError = (n: number, bounds: CatalightTierBounds): string | undefined => {
  if (bounds.exact) {
    return n === bounds.min ? undefined : `Must be exactly ${String(bounds.min)} hrs/mo`;
  }
  if (n < bounds.min || n > bounds.max) {
    return `Must be between ${String(bounds.min)} and ${String(bounds.max)} hrs/mo`;
  }
  return undefined;
};

// Resolves the error for a single Updated Offer hour input. `includeRequired`
// adds the empty-field error (only surfaced after a submit attempt); `bounds` adds
// the clinical-recommendation range/exact check. The read-only Clinical
// Recommendations themselves are never validated.
const hourError = (
  value: string,
  includeRequired: boolean,
  bounds: CatalightTierBounds | undefined,
): string | undefined => {
  if (value.trim() === "") return includeRequired ? "This field is required" : undefined;
  const numeric = numericError(value);
  if (numeric) return numeric;
  // numericError passed, so toNum returns a finite, non-negative number.
  return bounds ? boundsError(toNum(value)!, bounds) : undefined;
};

interface DialogErrors {
  date?: string;
  /** Form-level error not tied to a single field (e.g. no tiers to offer hours for). */
  general?: string;
  offer: Partial<Record<CatalightTierKey, string>>;
}

const hasAnyError = (e: DialogErrors): boolean =>
  Boolean(e.date) || Boolean(e.general) || Object.values(e.offer).some(Boolean);

// Validation is scoped to the editable Updated Offer. `includeRequired` is true
// when deciding whether to block submit; for live display callers pass
// `submitAttempted` so numeric errors show as the user types while "Required"
// only appears after a submit attempt.
const computeDialogErrors = (
  treatmentOffered: boolean,
  offerDate: string,
  offers: Record<CatalightTierKey, string>,
  visibleTierKeys: CatalightTierKey[],
  boundsByTier: Map<CatalightTierKey, CatalightTierBounds>,
  includeRequired: boolean,
): DialogErrors => {
  const offer: Partial<Record<CatalightTierKey, string>> = {};
  if (!treatmentOffered) return { offer };

  const date = includeRequired && offerDate.trim() === "" ? "This field is required" : undefined;
  // Offering treatment with no resolvable tiers would persist an all-null hours
  // payload. The hour inputs aren't rendered in that case, so there's nothing for
  // the clinician to fill in — block submission with guidance instead.
  const general =
    includeRequired && visibleTierKeys.length === 0
      ? "Recommended hours couldn't be loaded for this report. Reopen the report to confirm the treatment options, or contact support before submitting an offer."
      : undefined;
  for (const key of visibleTierKeys) {
    offer[key] = hourError(offers[key], includeRequired, boundsByTier.get(key));
  }
  return { date, general, offer };
};

// Scrolls the dialog to the first errored field so it isn't hidden below the
// fold. Inputs use their `name` as the element id; errors only occur when
// treatment is offered, so those inputs are always rendered.
const scrollToFirstError = (errors: DialogErrors, visibleTierKeys: CatalightTierKey[]): void => {
  const firstErrorName = errors.date ? "offer-date" : `offer-${visibleTierKeys.find((key) => errors.offer[key])}`;
  requestAnimationFrame(() => {
    document.getElementById(firstErrorName)?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};

// Builds the payor-metadata mutation input. When treatment isn't offered, the
// date and hours are cleared so a prior offer's values aren't carried into
// submission.
const buildUpsertInput = (
  reportId: string,
  treatmentOffered: boolean,
  offerDate: string,
  offers: Record<CatalightTierKey, string>,
  comments: string,
) => ({
  reportId,
  treatmentOffered,
  firstTreatmentOfferedDate: treatmentOffered ? offerDate || null : null,
  paraprofessionalHours: treatmentOffered ? toNum(offers.para) : null,
  midLevelHours: treatmentOffered ? toNum(offers.mid) : null,
  highLevelHours: treatmentOffered ? toNum(offers.high) : null,
  groupCare: treatmentOffered ? toNum(offers.group) : null,
  comments: comments.trim() || null,
});

// Mosaic's Dialog auto-focuses the first focusable element on open (its
// `initialFocus` ref is never attached), which scrolls a mid-dialog input into
// view; scroll-anchoring after the export data loads can do the same. Rather than
// race those, pin every scrollable ancestor to the top each frame until the user
// actually interacts (or a short grace period passes), then release so scrolling
// works normally. Re-runs when recommendations finish loading so the late layout
// shift is re-pinned.
const usePinDialogScrollToTop = (
  open: boolean,
  recommendationsLoading: boolean,
  contentRef: React.RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    if (!open) return;
    let pinning = true;
    let raf = 0;
    const pinToTop = () => {
      if (!pinning) return;
      let el: HTMLElement | null = contentRef.current?.parentElement ?? null;
      while (el) {
        if (el.scrollHeight > el.clientHeight) el.scrollTop = 0;
        el = el.parentElement;
      }
      raf = requestAnimationFrame(pinToTop);
    };
    const release = () => {
      pinning = false;
    };
    raf = requestAnimationFrame(pinToTop);
    const grace = setTimeout(release, 500);
    const opts = { once: true, passive: true } as const;
    globalThis.addEventListener("wheel", release, opts);
    globalThis.addEventListener("touchstart", release, opts);
    globalThis.addEventListener("pointerdown", release, opts);
    globalThis.addEventListener("keydown", release, { once: true });
    return () => {
      pinning = false;
      cancelAnimationFrame(raf);
      clearTimeout(grace);
      globalThis.removeEventListener("wheel", release);
      globalThis.removeEventListener("touchstart", release);
      globalThis.removeEventListener("pointerdown", release);
      globalThis.removeEventListener("keydown", release);
    };
  }, [open, recommendationsLoading, contentRef]);
};

// Appends a red asterisk to an Input's label so required fields read as required
// without waiting for inline validation. Targets the inner <label> the Input's
// Label component renders (the outer <label> wraps it), so only one "*" appears.
const REQUIRED_LABEL_CLASS =
  "[&_label_label]:after:ml-0.5 [&_label_label]:after:text-red-500 [&_label_label]:after:content-['*']";

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-limestone-600">{children}</p>
);

// Inline field error. The Input's built-in error slot reserves vertical space
// even when empty, so we suppress it (`formNoValidate`) and render this only when
// there's an error — keeping the default state tightly stacked.
const FieldError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-1 text-sm text-red-500">{children}</p>
);

// One label/value line in the read-only Clinical Recommendations card. Label and
// value each stay on a single line (no wrapping); a faint underline separates
// rows for easier scanning.
const RecRow: React.FC<{ label: string; value: string; muted?: boolean }> = ({ label, value, muted }) => (
  <div className="flex items-baseline justify-between gap-12 border-b border-limestone-200/60 py-2 last:border-b-0">
    <Paragraph size="small" colorType="primary" className="!font-semibold whitespace-nowrap">
      {label}
    </Paragraph>
    <Paragraph size="small" colorType={muted ? "tertiary" : "primary"} className="whitespace-nowrap text-right">
      {value}
    </Paragraph>
  </div>
);

const TierGroup: React.FC<{ label: string; tiers: CatalightTierRecommendation[]; keyPrefix: string }> = ({
  label,
  tiers,
  keyPrefix,
}) =>
  tiers.length === 0 ? null : (
    <>
      <SectionLabel>{label}</SectionLabel>
      {tiers.map((tier) => (
        <RecRow key={`${keyPrefix}-${tier.key}`} label={REC_ROW_LABELS[tier.key]} value={tier.hoursText} />
      ))}
    </>
  );

// Renders the hours rows for the Clinical Recommendations card.
// Extracted to avoid nested ternary chains (SonarCloud cognitive complexity).
// The `tiers` fallback is intentionally absent: practitionerTiers, caregiverTiers,
// and the merged tiers are all filtered by the same `allowed` set, so if both
// display groups are empty the merged list is empty too — no fallback needed.
const renderTierGroups = (
  serviceDisplay: ServiceDisplay,
  practitionerTiers: CatalightTierRecommendation[],
  caregiverTiers: CatalightTierRecommendation[],
): React.ReactNode => {
  if (serviceDisplay === "practitioner-only") {
    return <TierGroup label="Practitioner-Mediated" tiers={practitionerTiers} keyPrefix="p" />;
  }
  if (serviceDisplay === "caregiver-only") {
    return <TierGroup label="Caregiver-Mediated" tiers={caregiverTiers} keyPrefix="c" />;
  }
  // "both": show whichever groups have data
  return (
    <>
      {practitionerTiers.length > 0 && (
        <TierGroup label="Practitioner-Mediated" tiers={practitionerTiers} keyPrefix="p" />
      )}
      {caregiverTiers.length > 0 && <TierGroup label="Caregiver-Mediated" tiers={caregiverTiers} keyPrefix="c" />}
    </>
  );
};

// Read-only Clinical Recommendations card (service type + per-tier hours derived
// from the report). Shows a loading row until the export data resolves, then the
// service type (or an "unavailable" fallback) followed by tier hours.
const ClinicalRecommendationsCard: React.FC<{
  loading: boolean;
  serviceType: string | null;
  serviceDisplay: ServiceDisplay;
  practitionerTiers: CatalightTierRecommendation[];
  caregiverTiers: CatalightTierRecommendation[];
}> = ({ loading, serviceType, serviceDisplay, practitionerTiers, caregiverTiers }) => {
  if (loading) {
    return <RecRow label="Service Recommended:" value="Loading recommendations…" muted />;
  }
  return (
    <>
      <RecRow label="Service Recommended:" value={serviceType ?? "Service type unavailable"} muted={!serviceType} />
      {renderTierGroups(serviceDisplay, practitionerTiers, caregiverTiers)}
    </>
  );
};

// Error banners shown above the footer CTAs: the validation summary (a specific
// "no tiers" message takes precedence over the generic "fix the errors" one) and
// the payor submission failure passed down from the parent.
const SubmissionAlerts: React.FC<{
  submitAttempted: boolean;
  errors: DialogErrors;
  submissionError: boolean;
  submissionErrorMessage: string;
}> = ({ submitAttempted, errors, submissionError, submissionErrorMessage }) => (
  <>
    {submitAttempted && errors.general ? (
      <Alert appearance="error" title={errors.general} className="rounded-lg" />
    ) : null}
    {submitAttempted && !errors.general && hasAnyError(errors) ? (
      <Alert appearance="error" title="Please fix the errors before submitting." className="rounded-lg" />
    ) : null}
    {submissionError ? (
      <Alert
        appearance="error"
        title="Your report couldn't be submitted"
        text={
          <p className="font-sans font-normal">
            {submissionErrorMessage ||
              "Your report hasn't been sent — please try again. If the issue persists, contact support."}
          </p>
        }
        className="rounded-lg"
      />
    ) : null}
  </>
);

interface CatalightSubmissionDialogProps {
  open: boolean;
  reportId: string;
  clientName: string;
  dateOfBirth?: string;
  reportTypeLabel?: string;
  /** Runs the actual payor submission workflow after the metadata is saved. */
  onSubmitToPayor: () => void | Promise<void>;
  onViewReport: () => void;
  onClose: () => void;
  isSubmitting?: boolean;
  /** Gates only the submit action (e.g. export not ready) — "View report" stays enabled. */
  isSubmitDisabled?: boolean;
  submissionError?: boolean;
  submissionErrorMessage?: string;
}

export const CatalightSubmissionDialog: React.FC<CatalightSubmissionDialogProps> = ({
  open,
  reportId,
  clientName,
  dateOfBirth,
  reportTypeLabel = "Initial Assessment",
  onSubmitToPayor,
  onViewReport,
  onClose,
  isSubmitting = false,
  isSubmitDisabled = false,
  submissionError = false,
  submissionErrorMessage = "",
}) => {
  // Service type and per-tier hours are read-only values derived from the
  // report's export data (BE does not expose them as clean fields — see
  // catalightServiceType.ts). The query is gated on Completed status and is
  // already fetched by the parent page, so this resolves from cache.
  const { data: exportData, isLoading } = useReportExportDataQuery({ reportId }, { enabled: open && !!reportId });
  const { serviceType, tiers, practitionerTiers, caregiverTiers } = resolveCatalightRecommendations(
    exportData?.reportExportData?.sections,
  );
  const serviceDisplay = resolveServiceDisplay(serviceType);
  const recommendationsLoading = isLoading && !exportData;

  // Choose the tier set that matches what's shown in the display groups so the
  // clinician is validated against the same hours they see as recommended.
  const offerTiersByDisplay: Record<ServiceDisplay, CatalightTierRecommendation[]> = {
    "practitioner-only": practitionerTiers,
    "caregiver-only": caregiverTiers,
    both: tiers,
  };
  const offerTiers = offerTiersByDisplay[serviceDisplay];
  const visibleTierKeys = offerTiers.map((tier) => tier.key);
  const boundsByTier = new Map<CatalightTierKey, CatalightTierBounds>(
    offerTiers.map((tier) => [tier.key, tier.bounds]),
  );

  const { mutateAsync: upsertReportPayorMetadata, isPending: isSavingMetadata } = useUpsertReportPayorMetadataMutation(
    {},
  );

  const contentRef = useRef<HTMLDivElement>(null);
  // Tracks the latest `open` so an in-flight submit can bail if the dialog was
  // dismissed mid-save — the component stays mounted, so a stale await must not
  // start the payor workflow after the user closed it.
  const openRef = useRef(open);

  // Per product direction the Updated Offer is NOT prefilled from any prior save —
  // the clinician fills in treatment-offered, date, hours, and comments fresh each
  // time. Values persist while the dialog stays open (so a failed submission keeps
  // what was entered) and reset on each fresh open (e.g. a resubmission).
  const [treatmentOffered, setTreatmentOffered] = useState<boolean>(true);
  const [offerDate, setOfferDate] = useState<string>("");
  const [offers, setOffers] = useState<Record<CatalightTierKey, string>>({ para: "", mid: "", high: "", group: "" });
  const [comments, setComments] = useState<string>("");
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

  // When "No treatment recommended" is selected the offer inputs (radio, date,
  // hours) are hidden — treatment is implicitly not offered. Comments still show.
  const noTreatment = isNoTreatmentRecommended(serviceType);
  const effectiveTreatmentOffered = noTreatment ? false : treatmentOffered;

  usePinDialogScrollToTop(open, recommendationsLoading, contentRef);

  // Reset the editable offer on each fresh open so a prior submission's values
  // never carry into a resubmission. A failed submission keeps the dialog open
  // (no false→true transition), so in-progress entry is preserved there.
  useEffect(() => {
    openRef.current = open;
    if (!open) return;
    setTreatmentOffered(true);
    setOfferDate("");
    setOffers({ para: "", mid: "", high: "", group: "" });
    setComments("");
    setSubmitAttempted(false);
  }, [open]);

  // Surface (without blocking submission) when no service type could be resolved
  // from the report data. Don't warn while the export data is still loading.
  useEffect(() => {
    if (open && !recommendationsLoading && !serviceType) {
      console.warn(
        "[CatalightSubmissionDialog] Service type unavailable: report has no practitioner- or caregiver-mediated service value.",
      );
    }
  }, [open, recommendationsLoading, serviceType]);

  const errors = computeDialogErrors(
    effectiveTreatmentOffered,
    offerDate,
    offers,
    visibleTierKeys,
    boundsByTier,
    submitAttempted,
  );

  const isSubmittingAny = isSavingMetadata || isSubmitting;

  const handleSubmit = async () => {
    if (isSubmittingAny || isSubmitDisabled) return;

    const nextErrors = computeDialogErrors(
      effectiveTreatmentOffered,
      offerDate,
      offers,
      visibleTierKeys,
      boundsByTier,
      true,
    );
    if (hasAnyError(nextErrors)) {
      setSubmitAttempted(true);
      scrollToFirstError(nextErrors, visibleTierKeys);
      return;
    }

    // Persist the payor metadata first; the submission workflow reads it back as
    // `updatedTreatmentOfferedDetails`. The mutation reports failure via
    // `status: false` (not just by throwing), so we check both before letting the
    // payor submission start without saved data.
    try {
      const result = await upsertReportPayorMetadata({
        input: buildUpsertInput(reportId, effectiveTreatmentOffered, offerDate, offers, comments),
      });
      if (!result.upsertReportPayorMetadata?.status) {
        notifyError(
          result.upsertReportPayorMetadata?.message || "We couldn't save your submission details. Please try again.",
        );
        return;
      }
    } catch {
      notifyError("We couldn't save your submission details. Please try again.");
      return;
    }

    // If the dialog was dismissed while the save was in flight, don't start the
    // payor workflow behind the user's back.
    if (!openRef.current) return;

    // The parent runs the payor submission workflow and owns the dialog's
    // open/progress/error state from here.
    await onSubmitToPayor();
  };

  return (
    <Dialog
      open={open}
      setOpen={(next) => {
        if (!next) onClose();
      }}
      title=""
      size="xl"
      showCloseButton
      primaryButton={undefined}
      secondaryButton={undefined}
      overflowBehavior="yAuto"
      bodyClassName="!px-8 max-h-[72vh]"
      dialogClassName="!max-w-[780px] sm:!max-w-[780px]"
    >
      <div ref={contentRef} className="flex flex-col gap-4 pb-2">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Headline as="h2" size="large" colorType="primary">
              Report Submission Details
            </Headline>
            <Paragraph size="small" colorType="secondary">
              A few fields are required before this report can be sent to Catalight. These will be included with your
              submission.
            </Paragraph>
          </div>

          {/* Client meta */}
          <div className="flex flex-col gap-2 rounded-lg border border-limestone-200 bg-white p-4 ph-no-capture">
            <Paragraph size="medium" colorType="primary">
              {clientName}
            </Paragraph>
            <div className="flex flex-col">
              {dateOfBirth ? (
                <Paragraph size="small" colorType="secondary">
                  DOB: {dateOfBirth}
                </Paragraph>
              ) : null}
              <Paragraph size="small" colorType="secondary">
                Report: {reportTypeLabel}
              </Paragraph>
            </div>
          </div>
        </div>

        {/* Section 1 — Clinical recommendations (read-only, derived from the report) */}
        <div className="flex flex-col gap-3 rounded-lg bg-limestone-50 p-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-limestone-900">Clinical Recommendations</p>
            <Paragraph size="small" colorType="secondary">
              Service type and hours per month recommended for each tier based on your report. These values can only be
              edited within the report.
            </Paragraph>
          </div>
          <div className="flex flex-col">
            <ClinicalRecommendationsCard
              loading={recommendationsLoading}
              serviceType={serviceType}
              serviceDisplay={serviceDisplay}
              practitionerTiers={practitionerTiers}
              caregiverTiers={caregiverTiers}
            />
          </div>
        </div>

        {/* Section 2 — Updated offer (editable) */}
        <div className="flex flex-col gap-3">
          {noTreatment ? null : <SectionLabel>Updated offer</SectionLabel>}

          {/* Default state keeps the inputs tightly stacked (~8px). Inline error
              messages push siblings down only after a submit attempt, which is
              acceptable. */}
          <div className="flex flex-col gap-2">
            {noTreatment ? null : (
              <>
                <RadioInput
                  title="Treatment offered?"
                  legend="Treatment offered?"
                  name="treatment-offered"
                  items={[
                    { id: "yes", title: "Yes" },
                    { id: "no", title: "No" },
                  ]}
                  selectedId={treatmentOffered ? "yes" : "no"}
                  onChange={(e) => setTreatmentOffered(e.target.value === "yes")}
                  formNoValidate
                />

                {treatmentOffered ? (
                  <>
                    <div className={REQUIRED_LABEL_CLASS}>
                      <Input
                        label="First treatment offered date"
                        name="offer-date"
                        type="date"
                        inputSize="full"
                        value={offerDate}
                        onChange={(e) => setOfferDate(e.target.value)}
                        inputClassName={clsx(errors.date && "!ring-red-500")}
                        formNoValidate
                      />
                      {errors.date ? <FieldError>{errors.date}</FieldError> : null}
                    </div>

                    <div className={clsx("grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3", REQUIRED_LABEL_CLASS)}>
                      {visibleTierKeys.map((key) => {
                        const offerError = errors.offer[key];
                        return (
                          <div key={key}>
                            <Input
                              label={OFFER_INPUT_LABELS[key]}
                              name={`offer-${key}`}
                              type="number"
                              inputSize="full"
                              value={offers[key]}
                              onChange={(e) => setOffers((prev) => ({ ...prev, [key]: e.target.value }))}
                              inputClassName={clsx(offerError && "!ring-red-500")}
                              formNoValidate
                            />
                            {offerError ? <FieldError>{offerError}</FieldError> : null}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </>
            )}

            {/* Comments are optional and shown regardless of the treatment-offered answer. */}
            <div className="[&_textarea]:!text-sm [&_textarea]:placeholder:!text-limestone-600">
              <TextArea
                label="Comments"
                name="offer-comments"
                rows={3}
                placeholder="Add any notes for Catalight's review team..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sticky footer keeps the CTAs (and the error banner) in view while the
          form body scrolls. -mx-8/px-8 cancels the body's horizontal padding so
          the divider spans edge-to-edge. */}
      <div className="sticky bottom-0 -mx-8 flex flex-col gap-3 rounded-b-2xl border-t border-limestone-100 bg-white px-8 pb-6 pt-4">
        <SubmissionAlerts
          submitAttempted={submitAttempted}
          errors={errors}
          submissionError={submissionError}
          submissionErrorMessage={submissionErrorMessage}
        />
        <div className="flex items-center justify-between gap-6">
          <Paragraph size="extra-small" colorType="tertiary">
            By submitting, you're confirming this report is accurate and ready for payor review.
          </Paragraph>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="primary"
              buttonStyle="border"
              size="md"
              text="View report"
              onClick={onViewReport}
              disabled={isSubmittingAny}
            />
            <Button
              variant="primary"
              buttonStyle="filled"
              size="md"
              text={
                <div className="flex items-center justify-center gap-2">
                  {isSubmittingAny ? <Loader className="h-4 w-4" /> : null}
                  Submit to Catalight
                </div>
              }
              onClick={handleSubmit}
              disabled={isSubmittingAny || isSubmitDisabled}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};
